/*
  NB-edit.js — shared AI Edit engine for the members-portal AI surfaces.

  WHY THIS FILE EXISTS
  ai-chat-sandbox.js's Edit affordance (runEdit) gates on, and calls,
  window.nbEditRequest — which is defined GLOBALLY only in
  bySize_popular.js, and that file loads only on the by-size PDP. The two
  members-portal AI surfaces don't load it:
    • Portal Design Studio  (/pages/free-design-tools, design-tool-widget.liquid):
      v0-script.js defines an equivalent nbEditRequest but scope-locally
      (inside its $(document).ready()), never on window.
    • Portal AI Image Generator (/pages/ai-image-generator, ai-image-creator.liquid):
      no copy at all.
  So the capability gate (typeof window.nbEditRequest === 'function') stays
  off on both, and Edit can't run there.

  This tiny asset exposes window.nbEditRequest (+ the daily-cap gate
  window.canUseAiRequest) on those two portal surfaces. It is loaded via script_tag
  from design-tool-widget.liquid + ai-image-creator.liquid — the same two sections
  that eager-load ai-create.css. Neither section renders on the by-size PDP,
  theme.liquid, or the header, so this NEVER loads on the PDP's anonymous path:
  on the PDP, window.nbEditRequest keeps coming solely from bySize_popular.js.

  PROVENANCE: nbEditRequest posts to the bg-removal proxy Lambda
  (model 'gram-edit'), which calls Gram's edit-lite API server-side and
  returns a persistent imgix URL. The cap cluster (AI_DAILY_LIMIT / getLocalDayKey /
  getAiUsage / canUseAiRequest) is copied verbatim from bySize_popular.js, keyed
  on the same localStorage key nbUsage (per-origin; a legacy 'nanoBananaUsage'
  value is still read as a one-release fallback for continuity) — so the 50/day
  gate is consistent across every AI surface.

  ⚠ Deliberately NO consumeAiRequest here: the create-with-AI edit path gates on
  canUseAiRequest but does NOT increment (matching bySize_popular.js's existing
  behavior — the counter is bumped by the legacy edit-modal flow, not by runEdit).
  This asset is a consistent GATE, not a shared decrement. Changing that is out of scope.

  Wrapped in an IIFE + guarded window assignment so it can never clobber an
  already-present global or collide with another file's top-level declarations.
*/
/* UK 2026-09-14 — presign fallback for the surfaces this file serves.
   ai-chat-sandbox.js re-hosts every Edit result onto the store's own bucket via
   window.getPresignedUploadUrl(key, type) -> { url, sourceUrl } -> PUT. On the PDP
   / Design Studio that global comes from the upload engines (pdp_transferBySize.js,
   bySize_popular.js, v0-script.js); /pages/ai-image-generator loads none of them,
   so Edit died with "getPresignedUploadUrl unavailable" after the Lambda had
   already answered. Same UK uploads API route the engines call; defined only
   when nothing else has. */
(function () {
  if (typeof window.getPresignedUploadUrl === 'function') return;
  var API = 'https://k7ylxp3inc.execute-api.eu-west-2.amazonaws.com/production';
  window.getPresignedUploadUrl = function (fileName, fileType) {
    return fetch(API + '/uploads/getPresignedUploadUrl?file_name=' + encodeURIComponent(fileName) + '&file_type=' + encodeURIComponent(fileType || 'image/png'))
      .then(function (r) {
        if (!r.ok) throw new Error('presign failed (' + r.status + ')');
        return r.json();
      });
  };
})();
(function () {
  var aiJobAbortController = null;
  var aiJobCancelled = false;

  // ── nbEditRequest — Gram edit-lite via the bg-removal proxy Lambda ──
  async function nbEditRequest(imageUrl, promptText) {
    aiJobCancelled = false;
    console.log ( 'promptText', promptText );
    aiJobAbortController = new AbortController();

    const resp = await fetch('https://ruhg140mmf.execute-api.us-east-1.amazonaws.com/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'gram-edit-45g',
        imageUrl: imageUrl,
        prompt: promptText,
        transparency: 'AUTO'
      }),
      signal: aiJobAbortController.signal
    });

    if (!resp.ok) {
      const txt = await resp.text().catch(() => '');
      throw new Error('AI edit request failed (' + resp.status + '): ' + txt.slice(0, 200));
    }

    if (aiJobCancelled) {
      throw new Error("AI request cancelled by user");
    }

    const data = await resp.json();
    if (!data || !data.url) throw new Error('AI edit response missing url');
    return data.url;
  }

  // ── daily-cap GATE — VERBATIM cluster from bySize_popular.js (keyed on nbUsage) ──
  const AI_DAILY_LIMIT = 50;

  function getLocalDayKey() {
    // YYYY-MM-DD in USER'S local timezone
    const d = new Date();
    const year  = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day   = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function getAiUsage() {
    const day = getLocalDayKey();
    const raw = (localStorage.getItem('nbUsage') || localStorage.getItem('nanoBananaUsage'));
    let obj = {};
    try { obj = raw ? JSON.parse(raw) : {}; } catch (e) { obj = {}; }

    if (!obj || obj.day !== day) obj = { day, count: 0 };
    return obj;
  }

  function canUseAiRequest() {
    const usage = getAiUsage();
    return usage.count < AI_DAILY_LIMIT;
  }

  // Expose on window — guarded so we never clobber an already-present global
  // (e.g. bySize_popular.js's on the PDP, though this asset never loads there).
  if (typeof window.nbEditRequest !== 'function') {
    window.nbEditRequest = nbEditRequest;
  }
  if (typeof window.canUseAiRequest !== 'function') {
    window.canUseAiRequest = canUseAiRequest;
  }
})();
